import { Mfl } from '@deqo/mfl-js';
import { Mql } from '@deqo/mql-js';
import { darken } from 'polished';
import React, { useCallback, useState } from 'react';
import AutosizeInput from 'react-input-autosize';
import styled from 'styled-components';
import { CommandDefinition } from '../../../concepts/commands/CommandDefinition';
import { CommandRegistry } from '../../../concepts/commands/CommandRegistry';
import { ComponentType, ObjectManager } from '../../../concepts/objects';
import { Icon } from '../../../concepts/resources/Icon';
import { Colors, Input, toRem } from '../../../ui';
import { DropButton } from '../../../ui/DropButton';
import { useResolve } from '../../Container';
import { Physical } from '../../canvas/entities/Physical';
import { useComponentObserver } from '../../canvas/visualization/ComponentObserver';
import { CodeEditorStatus } from '../../common/CodeEditorStatus';
import { MflEditor } from '../../common/MflEditor';
import { MqlEditor, } from '../../common/MqlEditor';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { useMqlCompleter } from '../lens/MqlUtils';
import { CommandDispatchSettings } from './CommandDispatchSettings';
import { CommandSelector } from './CommandSelector';
import { ActionComputeItem, AssertComputeItem, ComputeItem, FormulaComputeItem, QueryComputeItem } from './ComputeItem';

const XAutosizeInput = AutosizeInput as any;

const ItemGrid = styled.div`
    // shrink srhink grow grid
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
`;

const typeColors = {
    query: Colors.positive,
    formula: Colors.mystery,
    action: Colors.observe,
    assert: Colors.negative,
};

const ItemHeader = styled.div`
    position: absolute;
    top: -.5rem;
    left: .5rem;
    z-index: 1;
    display: inline-flex;
    flex-direction: row;
    align-items: center;
    padding: .125rem .25rem;
    gap: .5rem;
    color: white;
    border-radius: .25rem;
    font-size: .75rem;
    font-weight: 450;
`;

const ItemInner = styled.div`
    display: grid;
    grid-template-columns: 1fr auto;
    gap: .5rem;
    border-radius: .5rem;
    padding: .25rem;
`;

const ItemActionsFrame = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    color: ${Colors.textLight};
    opacity: .2;
    transition: opacity .15s;
`;

const ItemFrame = styled.div<{ type: string }>`
    position: relative;
    padding-top: .5rem;

    ${ItemInner} {
        border: solid .1875rem ${p => typeColors[p.type]};
    }

    ${ItemHeader} {
        background: ${p => typeColors[p.type]};
    }

    &:hover {
        ${ItemActionsFrame} {
            opacity: 1;
        }
    }
`;

const ItemType = styled.div`
    display: inline-block;
    flex-direction: row;
    align-items: center;
    gap: .5rem;

    svg {
        width: 1rem;
        height: 1rem;
    }
`;

const ItemName = styled.div`
    input {
        background: transparent;
        border: none;
        font-size: .75rem;
        font-weight: 450;
        font-family: "Fira Code", "Courier New", monospace;
        color: #fff;
    }
`;

const ItemInput = styled.div`
    position: relative;
    padding: .25rem;
`;

const ItemButton = styled(DropButton)`
    padding: .25rem .375rem;
    margin-left: .0625rem;
    font-size: .75rem;
    color: ${Colors.primary};
    font-weight: 450;
    cursor: pointer;

    &:hover { color: ${darken(0.05, Colors.primary)} }
    &:active { color: ${darken(0.10, Colors.primary)} }
`;

const CodeEditorFrame = styled.div<{ height?: number }>`
    position: relative;
    min-width: 32rem; 
    height: ${p => toRem(p.height ?? 4)};
    background: white;
`;

const ItemNameEditor = ({ item }: { item: ComputeItem }) => {
    useComponentObserver(item);

    const objects = useResolve(ObjectManager);
    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        objects.update(u => {
            u.rolling(`edit:${item.id}:name`);
            item.name = e.target.value;
        });
    };

    return <XAutosizeInput value={item.name} onChange={onChange} />;
};

const ItemActions = ({ item }: { item: ComputeItem }) => {

    const objects = useResolve(ObjectManager);

    const onUp = () => {
        objects.update(() => {
            const prev = item.prevSibling();
            if (!!prev) {
                prev.addToParentBeforeSelf(item.removeSelf());
            }
        });
    };

    const onDown = () => {
        objects.update(() => {
            const next = item.nextSibling();
            if (!!next) {
                item.addToParentBeforeSelf(next.removeSelf());
            }
        });
    };

    const onDelete = () => {
        objects.update(u => {
            u.delete(item.removeSelf(), 'cascade');
        });
    };

    return <ItemActionsFrame>
        <DropButton>
            <Icon size="1" src="deqo://icons/hero/arrow-up" onClick={onUp} />
        </DropButton>
        <DropButton>
            <Icon size="1" src="deqo://icons/hero/x" onClick={onDelete} />
        </DropButton>
        <DropButton>
            <Icon size="1" src="deqo://icons/hero/arrow-down" onClick={onDown} />
        </DropButton>
    </ItemActionsFrame>;
};
const QueryItem = ({ item }: { item: QueryComputeItem }) => {
    useComponentObserver(item);
    const completer = useMqlCompleter();

    const objects = useResolve(ObjectManager);
    const [code, setCode] = useState(item.code);
    const lines = Math.max(1, code.split('\n').length);

    const onChange = useCallback((value: string) => {
        setCode(value);
        try {
            Mql.compile(value);
            objects.update(u => {
                u.rolling(`edit:${item.id}:value`);
                item.code = value;
            });
        }
        catch {
            // Ignore
        }
    }, []);

    return <ItemFrame type="query">
        <ItemHeader>
            <ItemType>
                <Icon src="deqo://icons/d2/compute-query" />
            </ItemType>
            <ItemName>
                <ItemNameEditor item={item} />
            </ItemName>
        </ItemHeader>
        <ItemInner>
            <ItemInput>
                <CodeEditorFrame height={1.5 * lines}>
                    <MqlEditor completer={completer} initialValue={code} onChange={onChange} />
                </CodeEditorFrame>
                <Flexer absolute top=".5" right="0">
                    <CodeEditorStatus lang={Mql} code={code} />
                </Flexer>
            </ItemInput>
            <ItemActions item={item} />
        </ItemInner>
    </ItemFrame>;
};

const FormulaItem = ({ item }: { item: FormulaComputeItem }) => {
    useComponentObserver(item);

    const objects = useResolve(ObjectManager);
    const [code, setCode] = useState(item.code);

    const onChange = useCallback((value: string) => {
        if (value.startsWith('=')) {
            value = value.slice(1);
        }
        setCode(value);
        try {
            Mfl.compile(value);
            objects.update(u => {
                u.rolling(`edit:${item.id}:value`);
                item.code = value;
            });
        }
        catch {
            // Ignore
        }
    }, []);

    return <ItemFrame type="formula">
        <ItemHeader>
            <ItemType>
                <Icon src="deqo://icons/d2/compute-formula" />
            </ItemType>
            <ItemName>
                <ItemNameEditor item={item} />
            </ItemName>
        </ItemHeader>
        <ItemInner>
            <ItemInput>
                <CodeEditorFrame>
                    <MflEditor initialValue={code} onChange={onChange} />
                </CodeEditorFrame>
                <CodeEditorStatus lang={Mfl} code={code} />
            </ItemInput>
            <ItemActions item={item} />
        </ItemInner>
    </ItemFrame>;
};

const ActionItem = ({ item }: { item: ActionComputeItem }) => {
    useComponentObserver(item);

    const objects = useResolve(ObjectManager);
    const registry = useResolve(CommandRegistry);
    const [name] = item.settings || [];

    const command = registry.get(name);

    const onChangeCommand = (commandDef: CommandDefinition) => {
        objects.update(u => {
            u.rolling(`edit:${item.id}:settings`);
            item.settings = [commandDef.name];
        });
    };

    const onChangeSettings = (settings: any[]) => {
        objects.update(u => {
            u.rolling(`edit:${item.id}:settings`);
            item.settings = settings;
        });
    };

    return <ItemFrame type="action">
        <ItemHeader>
            <ItemType>
                <Icon src="deqo://icons/hero-mini/bolt" />
            </ItemType>
            <ItemName>
                {command?.label ?? '...'}
            </ItemName>
        </ItemHeader>
        <ItemInner>
            <ItemInput>
                {!name && <CommandSelector onSelect={onChangeCommand} />}
                {!!name && <CommandDispatchSettings settings={item.settings} onChange={onChangeSettings} />}
            </ItemInput>
            <ItemActions item={item} />
        </ItemInner>
    </ItemFrame>;
};

const AssertItem = ({ item }: { item: AssertComputeItem }) => {
    useComponentObserver(item);

    const objects = useResolve(ObjectManager);

    const onChangeValue = (value: string) => {
        objects.update(u => {
            u.rolling(`edit:${item.id}:settings`);
            item.value = value;
        });
    };

    const onChangeMessage = (message: string) => {
        objects.update(u => {
            u.rolling(`edit:${item.id}:settings`);
            item.message = message;
        });
    };

    return <ItemFrame type="assert">
        <ItemHeader>
            <ItemType>
                <Icon src="deqo://icons/hero-mini/bolt" />
            </ItemType>
            <ItemName>
                <ItemNameEditor item={item} />
            </ItemName>
        </ItemHeader>
        <ItemInner>
            <ItemInput>
                <Input before="Check" compact value={item.value} onChange={onChangeValue} />
                <Input before="Message" compact value={item.message} onChange={onChangeMessage} />
            </ItemInput>
            <ItemActions item={item} />
        </ItemInner>
    </ItemFrame>;
};

const Item = ({ item }: { item: ComputeItem }) => <>
    {item.is(QueryComputeItem) && <QueryItem item={item.cast(QueryComputeItem)} />}
    {item.is(FormulaComputeItem) && <FormulaItem item={item.cast(FormulaComputeItem)} />}
    {item.is(ActionComputeItem) && <ActionItem item={item.cast(ActionComputeItem)} />}
    {item.is(AssertComputeItem) && <AssertItem item={item.cast(AssertComputeItem)} />}
</>;

export const ComputePanel = React.memo<{ object: Physical }>(({ object }) => {

    useComponentObserver(object);

    const objects = useResolve(ObjectManager);
    const items = object.polarize(ComputeItem);

    const onAdd = (type: ComponentType<ComputeItem>, toFront = false) => {
        objects.update(u => {
            const tm = u.create(type);
            tm.name = type.name;
            if (toFront) {
                object.addAtBack(tm);
            }
            else {
                object.add(tm);
            }
        });
    };

    return <Flexer>
        <Flexer flex="row" justify="end">
            <ItemButton overflow="0" onClick={() => onAdd(ActionComputeItem, true)}>
                <span>+ Action</span>
            </ItemButton>
            <ItemButton overflow="0" onClick={() => onAdd(AssertComputeItem, true)}>
                <span>+ Assert</span>
            </ItemButton>
            <ItemButton overflow="0" onClick={() => onAdd(QueryComputeItem, true)}>
                <span>+ Selector</span>
            </ItemButton>
            <ItemButton overflow="0" onClick={() => onAdd(FormulaComputeItem, true)}>
                <span>+ Formula</span>
            </ItemButton>
        </Flexer>
        <ItemGrid>
            {items.map(x => <Item key={x.id} item={x} />)}
        </ItemGrid>
        <Flexer flex="row" justify="end">
            <ItemButton overflow="0" onClick={() => onAdd(ActionComputeItem)}>
                <span>+ Action</span>
            </ItemButton>
            <ItemButton overflow="0" onClick={() => onAdd(AssertComputeItem)}>
                <span>+ Assert</span>
            </ItemButton>
            <ItemButton overflow="0" onClick={() => onAdd(QueryComputeItem)}>
                <span>+ Selector</span>
            </ItemButton>
            <ItemButton overflow="0" onClick={() => onAdd(FormulaComputeItem)}>
                <span>+ Formula</span>
            </ItemButton>
        </Flexer>
    </Flexer>;
});
